import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import {BrowserRouter} from 'react-router-dom';
import './i18n';
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {StompSessionProvider} from "react-stomp-hooks";
import {AppThemeProvider} from "./config/theme-context";
import {refreshAccessToken, startTokenRefreshLoop} from "./config/token-refresh";
import {initViewportTracking} from "./config/viewport";
import {registerServiceWorker} from "./config/push-notifications";
import {migrateLegacySession} from "./config/auth-session";
import AppErrorBoundary from "./config/error-boundary";

// Before anything reads a token: bring a session over from the old per-tab store.
migrateLegacySession();

const root = ReactDOM.createRoot(document.getElementById('root'));
const WS_URL = process.env.REACT_APP_WS_URL;
const queryClient = new QueryClient();

// Renew the 5-minute access token on a timer so the STOMP socket can always authenticate. Without
// this an idle operator's token expired, every reconnect was rejected with the same dead token, and
// realtime only returned on a full page reload.
startTokenRefreshLoop();

// Publishes the screen's real visible height (and the keyboard inset) as CSS variables, so the
// console can fill a phone screen without hiding its own reply box under the browser bar.
initViewportTracking();

// Register the Web Push service worker as early as possible so it is controlling the page by the
// time the operator signs in and we subscribe. Registration alone asks for nothing and rings
// nothing; the permission prompt and the subscription happen in the dashboard, on a click.
registerServiceWorker();

/**
 * Only opens the realtime socket once there is a token to authenticate it with.
 *
 * <p>Without this the provider connects the moment the app mounts — including on the login page,
 * where by definition nobody is signed in. Each of those connects is refused and retried every few
 * seconds for as long as the tab stays open, which is pointless work and, worse, buries the log in
 * "no authentication token supplied" warnings. That noise is not harmless: it is what made a real
 * operator's authentication failure hard to pick out of the same log.
 *
 * <p>Polls localStorage rather than taking a prop, because login writes the token from a screen
 * far below this provider and there is no shared auth store to subscribe to. A one-second poll on a
 * single string is cheap, and it also picks up sign-out.
 */
function RealtimeProvider({children}) {
    const [hasToken, setHasToken] = useState(() => !!localStorage.getItem('access-token'));

    useEffect(() => {
        const check = () => setHasToken(!!localStorage.getItem('access-token'));
        const id = setInterval(check, 1000);
        window.addEventListener('storage', check);
        return () => {
            clearInterval(id);
            window.removeEventListener('storage', check);
        };
    }, []);

    return (
        <StompSessionProvider
            url={WS_URL}
            // Signed out: do not connect at all.
            enabled={hasToken}
            // The STOMP CONNECT frame is now authenticated on the backend, so the
            // access token must ride along on connect. beforeConnect runs before every
            // (re)connect and reads localStorage fresh, so after login — or after a
            // token refresh — the next reconnect authenticates without a full reload.
            beforeConnect={(client) => {
                const token = localStorage.getItem('access-token');
                client.connectHeaders = token ? {Authorization: `Bearer ${token}`} : {};
            }}
            // A rejection here means the access token expired: renew it so the next automatic
            // retry (reconnectDelay below) authenticates, instead of re-sending the same dead
            // token every 3s forever. refreshAccessToken ends the session outright if the
            // refresh token is dead too, so this can no longer loop indefinitely.
            onStompError={(frame) => {
                console.warn('STOMP error:', frame?.headers?.message);
                refreshAccessToken();
            }}
            onWebSocketError={(evt) => console.warn('WebSocket connection error', evt)}
            // Frame-level tracing prints the CONNECT frame (bearer token) and every MESSAGE frame
            // (customer transcripts) to the console. Development only.
            debug={process.env.NODE_ENV === 'production' ? () => {} : (str) => console.debug(str)}
            reconnectDelay={3000}
            // Heartbeats, stated rather than inherited.
            //
            // A socket can die without either end being told — a laptop sleeping, moving between
            // access points, or a NAT quietly dropping an idle flow. The TCP connection still looks
            // open, so nothing fires onWebSocketClose and nothing triggers the reconnect above; the
            // operator watches a dead feed while believing they are online, which is exactly what
            // was reported: messages "arriving much later" on a laptop but instantly on a phone,
            // whose OS suspends and re-establishes the connection for it.
            //
            // heartbeatIncoming is the half that fixes it: with the server pinging every 10s
            // (WebSocketConfig.HEARTBEAT_MS), stompjs closes a connection it has heard nothing on
            // and reconnects.
            //
            // 25s, not 10s, and the difference is the whole point. stompjs checks on a setInterval
            // of `heartbeatIncoming` and gives up after `heartbeatIncoming * 2` of silence, so 10s
            // meant a socket died the moment two beats were missed. That is far too strict for two
            // ordinary situations: a browser throttling timers in a background tab — which every
            // modern browser does aggressively, and an operator working with the console behind
            // another window is the normal case, not the exception — and a few seconds of jitter on
            // the path.
            //
            // Measured on production before this change: one operator's browser opened 388 sockets
            // in a single hour, in bursts, with the gaps between them landing on multiples of ten —
            // the signature of this check firing on a connection that was never actually dead. A
            // probe against the server confirmed it was sending its heartbeats exactly on time, so
            // nothing was wrong with the socket; the client was killing it.
            //
            // At 25s the client tolerates 50s of silence — five consecutive missed beats — before
            // declaring the socket dead. A genuinely dead connection is still found within a minute
            // instead of never, which is what this setting exists for, and the HTTP fallbacks cover
            // that minute.
            heartbeatIncoming={25000}
            heartbeatOutgoing={10000}
            // When stompjs decides a socket is dead — a missed heartbeat, or the dashboard's
            // forceDisconnect() on return to the tab — treat it as gone at once instead of asking it
            // to close politely. On a genuinely dead socket (laptop lid, NAT timeout) nobody is left
            // to answer that close, so the browser waits out its closing-handshake timer (Chromium:
            // 60s) while the client still reports "connected" and swallows every publish. With this
            // set, the close event is synthesised immediately and reconnectDelay starts counting.
            discardWebsocketOnCommFailure={true}
        >
            {children}
        </StompSessionProvider>
    );
}

root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <BrowserRouter>
            <QueryClientProvider client={queryClient}>
                <AppThemeProvider>
                    <RealtimeProvider>
                        <App/>
                    </RealtimeProvider>
                </AppThemeProvider>
            </QueryClientProvider>
        </BrowserRouter>
      </AppErrorBoundary>
    </React.StrictMode>
);


reportWebVitals();
